import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import debounce from '../util/debounce.js';
import isMobile from '../util/mobile.js';
import { enableBodyScroll, disableBodyScroll } from '../util/bodyScroll.js';
import { disableInputsExcept, enableInputs } from '../util/disableInputs.js';

import './autocomplete.css';

const fetchDebounceDelay = 200;

export class Autocomplete extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            options: [],
            selectedOption: 0,
            focus: window.location.search === `?autocomplete=${this.props.id}`,
            fetching: false,
            clearOverlayInput: false,
            displayEmptyResultPlaceholder: false,
        };

        this.fetchCount = 0;

        this.selectCurrentOption = () => {
            if (this.state.selectedOption >= 0 && this.state.selectedOption < this.state.options.length) {
                this.onClickOption(this.state.options[this.state.selectedOption]);
            } else {
                this.setState({ focus: false });
            }
        };

        this.fetchOptions = debounce(value => {
            const fetchNum = ++this.fetchCount;

            this.setState({ fetching: true, focus: true });

            const encodedValue = encodeURIComponent(value);
            fetch(`${this.props.baseUrl}${encodedValue}`)
                .then(r => r.json())
                .then(r => {
                    if (fetchNum === this.fetchCount) {
                        const validResult = Array.isArray(r) && r.length;
                        const displayEmptyResultPlaceholder = !validResult && this.props.emptyResultPlaceholder;
                        const options = validResult ? this.props.mapResults(r) : this.state.options;
                        this.setState({
                            options: displayEmptyResultPlaceholder ? [] : options,
                            selectedOption: isMobile() ? -1 : 0,
                            fetching: false,
                            displayEmptyResultPlaceholder,
                        });
                    }
                })
                .catch(error => {
                    console.error(error); //eslint-disable-line
                    if (fetchNum === this.fetchCount) {
                        this.setState({ fetching: false });
                    }
                });
        }, fetchDebounceDelay);

        this.prefetchOptions = debounce(value => {
            const fetchNum = ++this.fetchCount;

            fetch(`${this.props.baseUrl}${value}`)
                .then(r => r.json())
                .then(r => {
                    if (fetchNum === this.fetchCount) {
                        const options = Array.isArray(r) && r.length ? this.props.mapResults(r) : this.state.options;
                        this.props.onChange(options[0]);
                    }
                })
                .catch();
        }, 10);

        this.onChange = ({ target: { value } }) => {
            this.props.onChangeText(value);
            this.setState({ focus: true, clearOverlayInput: false });

            if (value.length >= this.props.minChars) {
                this.fetchOptions(value);
            } else {
                this.setState(s => ({ options: isMobile() ? s.options : [], fetching: false, displayEmptyResultPlaceholder: false }));
            }
        };

        this.onClickOption = option => {
            this.props.onChange(option);
            enableInputs();
            if (this.props.popUrlContext) {
                this.props.popUrlContext();
            }
            this.setState({ focus: false, clearOverlayInput: false }, enableBodyScroll);
        };

        this.keyDown = e => {
            switch (e.key) {
                case 'ArrowDown':
                    this.setState(s => {
                        const selectedOption = (s.selectedOption + 1) % s.options.length;
                        return { selectedOption };
                    });
                    e.preventDefault();
                    break;

                case 'ArrowUp':
                    this.setState(s => {
                        const selectedOption = s.selectedOption <= 0 ? s.options.length - 1 : s.selectedOption - 1;
                        return { selectedOption };
                    });
                    e.preventDefault();
                    break;

                case 'Enter':
                    this.selectCurrentOption();
                    e.preventDefault();
                    break;

                case 'Tab':
                    this.selectCurrentOption();
                    break;

                case 'Escape':
                    this.setState({ options: [], selectedOption: 0 });
                    break;
            }
        };

        this.onFocus = event => {
            if (this.props.onFocus) {
                this.props.onFocus();
            }
            disableBodyScroll();
            event.target.select();
            disableInputsExcept(this.props.id);
            if (isMobile()) {
                if (this.props.pushUrlContext) {
                    this.props.pushUrlContext(`autocomplete=${this.props.id}`);
                }
            } else {
                window.addEventListener('keydown', this.keyDown);
            }
            this.setState({ focus: true, clearOverlayInput: false });
        };

        this.onBlur = () => {
            if (!isMobile()) {
                window.removeEventListener('keydown', this.keyDown);
                this.selectCurrentOption();
            }
        };

        this.transitionOverlay = () => {
            if (isMobile()) {
                if (window.history.state == null) {
                    // This means it's page load
                    return;
                }
                this.setState({ focus: window.location.search === `?autocomplete=${this.props.id}` });
                if (!(window.location.search === `?autocomplete=${this.props.id}`)) {
                    enableInputs();
                }
            }
        };

        window.addEventListener('popstate', this.transitionOverlay);
    }

    componentWillMount() {
        if (this.props.preFill !== null) {
            this.prefetchOptions(this.props.preFill);
        }
    }

    componentWillReceiveProps(newProps) {
        this.setState({ value: newProps.value });
    }

    componentWillUnmount() {
        this.fetchCount++; // Keep fetch requests that finish after the component has been unmounted from calling this.setState

        window.removeEventListener('popstate', this.transitionOverlay);
        window.removeEventListener('keydown', this.keyDown);
    }

    onClear() {
        this.setState({ clearOverlayInput: true }, () => {
            this.inputMobile.focus();
        });
    }

    onCancel() {
        this.setState({ focus: false }, () => {
            if (this.state.selectedOption === -1) {
                this.props.onChange();
            }
            if (isMobile() && this.props.popUrlContext) {
                this.props.popUrlContext();
            }
            enableInputs();
            enableBodyScroll();
        });
    }

    renderOptions() {
        const getClicker = option => e => {
            e.stopPropagation();
            this.onClickOption(option);
        };
        const getMouseOverer = optionIdx => () => this.setState({ selectedOption: optionIdx });
        const mobile = isMobile();
        let lastOptionType = '';

        if (this.state.options.length > 0 && this.state.focus) {
            return (
                <ul role={mobile ? undefined : 'listbox'} id={`${this.props.id}-options`} data-testid={`${this.props.id}-options`}>
                    {this.state.options.map((option, optionIdx) => {
                        let typeLabel = null;
                        let typeLabelLogo = null;
                        const cls = option.cls ? `${option.cls} ` : '';
                        if (
                            typeof option.type === 'string' &&
                            option.type.length > 0 &&
                            (option.type !== lastOptionType || optionIdx === 0)
                        ) {
                            const typeLabelLogoImg = `${FINN.travel.cdnBaseUrl}${FINN.travel.contextPath}${option.typeLogo}`;
                            const typeLabelImgCls = 'type-logo u-float-right u-pt2';
                            lastOptionType = option.type;
                            typeLabelLogo = option.typeLogo ? <img className={typeLabelImgCls} aria-hidden src={typeLabelLogoImg} /> : null;
                            typeLabel = (
                                <li className="type u-t4 u-stone u-mh0 u-mv8">
                                    {typeLabelLogo} {lastOptionType}
                                </li>
                            );
                        }
                        return (
                            <React.Fragment key={`el${option.id}`}>
                                {typeLabel}
                                <li
                                    role={mobile ? undefined : 'none'}
                                    className={`${cls}${this.state.selectedOption === optionIdx ? 'selected' : ''}`}
                                    onClick={getClicker(option)}
                                    onMouseOver={getMouseOverer(optionIdx)}>
                                    <a
                                        role={mobile ? undefined : 'option'}
                                        aria-selected={mobile ? undefined : this.state.selectedOption === optionIdx}
                                        id={`${this.props.id}-option${option.id}`}
                                        onClick={getClicker(option)}>
                                        {option.text}
                                    </a>
                                    {option.count ? <span className="u-float-right">{option.count}</span> : null}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            );
        } else if (this.state.displayEmptyResultPlaceholder && this.state.focus) {
            return (
                <ul role={mobile ? undefined : 'listbox'} id={`${this.props.id}-options`} data-testid={`${this.props.id}-options`}>
                    <li className="type u-stone u-mh0 u-mt8 u-mb2">{this.props.emptyResultPlaceholder}</li>
                </ul>
            );
        } else {
            return null;
        }
    }

    renderSpinner() {
        return (
            <div className={`spinner ${this.state.focus && this.state.fetching ? 'active' : 'inactive'}`}>
                <img
                    src={`${FINN.travel.cdnBaseUrl}${FINN.travel.contextPath}assets/img/spinner.svg`}
                    alt={this.props.t('autocomplete.fetching')}
                />
            </div>
        );
    }

    render() {
        const { id, value, placeholder, autoFocus, errorText } = this.props;
        const mobile = isMobile();
        const errorClass = errorText ? ' input--is-invalid' : '';
        const platformClass = mobile ? ' mobile' : ' desktop';
        const hasSelectedOption = this.state.options.length > 0 && this.state.focus && this.state.selectedOption !== -1;

        return (
            <div className={`input input--text autocomplete${platformClass}${errorClass}`}>
                <label htmlFor={id} className="u-truncate">
                    {placeholder}
                </label>
                <div
                    {...(!mobile && {
                        role: 'combobox',
                        'aria-owns': `${id}-options`,
                        'aria-expanded': hasSelectedOption,
                    })}>
                    <input
                        type="text"
                        id={id}
                        className="input-bg u-size1of1 u-truncate"
                        autoFocus={!mobile && autoFocus}
                        value={value}
                        data-testid={id}
                        autoComplete="off"
                        onChange={this.onChange}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        ref={field => (this.input = field)}
                        {...(!mobile && {
                            'aria-autocomplete': 'list',
                            'aria-activedescendant': hasSelectedOption
                                ? `${id}-option${this.state.options[this.state.selectedOption].id}`
                                : undefined,
                            'aria-controls': hasSelectedOption ? `${id}-options` : undefined,
                        })}
                    />
                </div>
                {errorText ? (
                    <div className="input__sub-text" role="alert">
                        <span className="input__sub-text__error">{errorText}</span>
                    </div>
                ) : null}
                {this.state.focus && mobile ? (
                    <div className="u-pa16 overlay">
                        <div className="top-row">
                            <div className="input input--text u-float-left input-wrapper">
                                <label htmlFor={`${id}-mobile-overlay`} className="u-screen-reader-only">
                                    {placeholder}
                                </label>
                                <input
                                    type="text"
                                    id={`${id}-mobile-overlay`}
                                    className="u-size1of1 u-pr32 u-truncate"
                                    autoFocus
                                    value={this.state.clearOverlayInput ? '' : value}
                                    placeholder={placeholder}
                                    data-testid={`${id}-mobile-overlay`}
                                    autoComplete="off"
                                    onChange={this.onChange}
                                    ref={field => (this.inputMobile = field)}
                                />
                                <button
                                    type="button"
                                    aria-label={this.props.t('common:action.reset')}
                                    className="input--search__button input--search__button--reset"
                                    onClick={this.onClear.bind(this)}
                                />
                                {this.renderSpinner()}
                            </div>
                            <button
                                type="button"
                                aria-label="Avbryt"
                                className="input--search__submit pad-override button button--flat u-float-right"
                                onClick={this.onCancel.bind(this)}>
                                {this.props.t('common:action.cancel')}
                            </button>
                        </div>
                        {this.renderOptions()}
                    </div>
                ) : (
                    <>
                        {this.renderOptions()}
                        {this.renderSpinner()}
                    </>
                )}
            </div>
        );
    }
}

Autocomplete.propTypes = {
    t: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    baseUrl: PropTypes.string.isRequired, // Requests will be made to `${baseUrl}${value}`
    mapResults: PropTypes.func.isRequired, // Receives result of requests, should return and array of options
    onChange: PropTypes.func.isRequired, // Receives the selected option exactly as returned from mapResults
    onChangeText: PropTypes.func.isRequired, // Must update the value or the field will be read only
    onFocus: PropTypes.func,

    placeholder: PropTypes.string,
    autoFocus: PropTypes.bool,
    minChars: PropTypes.number, // Minimum characters required to trigger a request
    errorText: PropTypes.string,
    preFill: PropTypes.string,
    emptyResultPlaceholder: PropTypes.string,
    pushUrlContext: PropTypes.func,
    popUrlContext: PropTypes.func,
};

Autocomplete.defaultProps = {
    placeholder: '',
    autoFocus: false,
    minChars: 1,
    errorText: '',
    preFill: null,
};

export default withTranslation('search')(Autocomplete);
